import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import type { ReactNode } from 'react'

import type { ApiError } from '../api'
import { useToasts } from '../hooks'
import { fmt, useT } from '../i18n'

import { Icon } from './Icon'

export function Spinner({ label }: { label?: string }) {
  const { t } = useT()

  return (
    <span
      className="spinner"
      role="status"
      aria-label={label ?? t.common.loading}
    />
  )
}

export function Loading() {
  return (
    <div className="center-fill">
      <Spinner />
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: IconDefinition
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      {icon && <Icon icon={icon} size="2x" />}
      <p className="empty__title">{title}</p>
      {children && <p style={{ maxWidth: 440 }}>{children}</p>}
      {action}
    </div>
  )
}

export function ErrorAlert({
  error,
  title,
}: {
  error: unknown
  title?: string
}) {
  const { t } = useT()

  if (!error) return null
  const e = error as Partial<ApiError> & { message?: string }

  return (
    <div className="alert alert--danger" role="alert">
      <div>
        <p className="alert__title">{title ?? t.common.somethingWrong}</p>
        <p>{e.message ?? String(error)}</p>
        {!!e.details?.length && (
          <ul>
            {e.details.map((d, i) => (
              <li key={i}>
                <code>{d.path}</code> — {d.message}
              </li>
            ))}
          </ul>
        )}
        {e.correlationId && (
          <p className="small muted">
            {fmt(t.common.reference, { id: e.correlationId })}
          </p>
        )}
      </div>
    </div>
  )
}

export function Toasts() {
  const { toasts, dismiss } = useToasts()
  const { t: tr } = useT()

  return (
    <div className="toasts" aria-live="polite" data-theme="dark">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast ${t.kind === 'error' ? 'toast--error' : ''}`}
          role={t.kind === 'error' ? 'alert' : 'status'}
        >
          <div className="grow">
            <p>{t.message}</p>
            {t.meta && <p className="toast__meta">{t.meta}</p>}
          </div>
          <button
            className="icon-btn"
            aria-label={tr.common.dismiss}
            onClick={() => dismiss(t.id)}
            style={{ width: 24, height: 24 }}
          >
            <Icon icon={faXmark} />
          </button>
        </div>
      ))}
    </div>
  )
}
